import React, { useState, useEffect, useRef } from 'react';
import { getFirestore, collection, addDoc, updateDoc, doc, getDoc, setDoc, query, where, getDocs, serverTimestamp, onSnapshot, limit, orderBy, deleteField, Timestamp } from 'firebase/firestore';
import { getAuth } from 'firebase/auth';
import { db, storage } from '../../firebase';
import ClaudeService from '../system/ClaudeService';
import '../styles/AIGenerator.css';
import { getFunctions, httpsCallable } from 'firebase/functions';
import Menu from '../../main/menu';
import axios from 'axios';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import { useCustomerSwitch } from '../../contexts/CustomerSwitchContext';
import { getEffectiveUserId, getEffectiveUserData } from '../../firebase';
import { useLanguage } from '../../contexts/LanguageContext';
import { getPromptByLanguage } from '../../languages/prompts/index';

// Tilføj en reference funktion til textarea for autoresizing
const resizeTextarea = (textarea) => {
  if (!textarea) return;
  textarea.style.height = 'auto';
  textarea.style.height = `${textarea.scrollHeight}px`;
};

const AIGenerator = ({ onLogout }) => {
  const [loading, setLoading] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [post, setPost] = useState(null);
  const [editing, setEditing] = useState(false);
  const [editText, setEditText] = useState('');
  const [selectedImage, setSelectedImage] = useState(null);
  const [galleryOpen, setGalleryOpen] = useState(false);
  const [galleryImages, setGalleryImages] = useState([]);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [successMessage, setSuccessMessage] = useState(null);
  const [errorMessage, setErrorMessage] = useState(null);
  const [userSettings, setUserSettings] = useState(null);
  const [aiInstructions, setAiInstructions] = useState('');
  const [aiEditing, setAiEditing] = useState(false);
  const [imageOptionsOpen, setImageOptionsOpen] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [isMenuCollapsed, setIsMenuCollapsed] = useState(false);
  const [error, setError] = useState(null);
  const [isSaving, setIsSaving] = useState(false);
  const [dialogLoading, setDialogLoading] = useState(false);
  const [dialogError, setDialogError] = useState(null);
  const [aiPromptInput, setAiPromptInput] = useState('');
  const [aiPromptDialogOpen, setAiPromptDialogOpen] = useState(false);
  const textareaRef = useRef(null);
  const fileInputRef = useRef(null);
  const cameraInputRef = useRef(null);
  const auth = getAuth();
  const functions = getFunctions();
  const { selectedCustomer, isWorkingOnBehalfOfCustomer } = useCustomerSwitch();
  const { t, language } = useLanguage();
  const [preCalculatedTimes, setPreCalculatedTimes] = useState([]);
  const [timeCalculationLoading, setTimeCalculationLoading] = useState(false);

  // Lyt til menu-collapse hændelser
  useEffect(() => {
    const handleMenuCollapse = (e) => {
      if (e.detail && e.detail.hasOwnProperty('isCollapsed')) {
        setIsMenuCollapsed(e.detail.isCollapsed);
      }
    };

    window.addEventListener('menuCollapse', handleMenuCollapse);
    return () => window.removeEventListener('menuCollapse', handleMenuCollapse);
  }, []);

  useEffect(() => {
    fetchUserSettings();
    
    // Indlæs billeder først
    const loadImagesAndThenPosts = async () => {
      await fetchUserImages();
      // Kun når billeder er indlæst, indlæs opslagene
      if (auth.currentUser) {
        const unsubscribe = fetchPosts();
        // Gem unsubscribe-funktionen, så vi kan rydde op senere
        window.postsUnsubscribe = unsubscribe;
      }
    };
    
    loadImagesAndThenPosts();
  }, [auth.currentUser, selectedCustomer]);
  
  // Tilføj useEffect til at håndtere oprydning af onSnapshot-lytter
  useEffect(() => {
    // Returner en oprydningsfunktion, der kaldes når komponenten unmounts
    return () => {
      // Hvis der er en aktiv posts-lytter, fjern den
      if (window.postsUnsubscribe && typeof window.postsUnsubscribe === 'function') {
        window.postsUnsubscribe();
      }
    };
  }, []);
  
  // FJERNET: Problematisk useEffect der automatisk tildelte billeder
  // Dette forhindrede brugere i at fjerne billeder fra opslag
  // Automatisk billede-tildeling sker nu kun i backend ved oprettelse af NYE opslag

  const fetchUserSettings = async () => {
    if (!auth.currentUser) return null;
    
    try {
      // Brug den effektive bruger-ID baseret på den valgte kunde
      const effectiveUserId = getEffectiveUserId(selectedCustomer);
      
      // Hent brugerdata fra user dokument
      const userRef = doc(db, 'users', effectiveUserId);
      const userDoc = await getDoc(userRef);
      
      // Hent AI-indstillinger inkl. postLanguage fra ai_prompts dokumentet
      const aiSettingsRef = doc(db, 'users', effectiveUserId, 'settings', 'ai_prompts');
      const aiSettingsDoc = await getDoc(aiSettingsRef);
      
      // Kombiner brugerdata med AI-indstillinger
      const userData = userDoc.exists() ? userDoc.data() : {};
      const aiSettings = aiSettingsDoc.exists() ? aiSettingsDoc.data() : {};
      
      // Kombiner dem til et samlet userSettings objekt
      const combinedSettings = {
        ...userData,
        ...aiSettings,
        // Sikr at disse AI-specifikke felter bevares
        products: aiSettings.products || userData.products || [],
        leadMagnets: aiSettings.leadMagnets || userData.leadMagnets || [],
        postLanguage: aiSettings.postLanguage || 'da' // Default til dansk hvis ikke angivet
      };
      
      console.log('Indlæst brugerindstillinger med sprog:', combinedSettings.postLanguage);
      
      // Opdater userSettings state
      setUserSettings(combinedSettings);
      
      return combinedSettings;
    } catch (error) {
      console.error('Fejl ved hentning af brugerindstillinger:', error);
      return null;
    }
  };

  const fetchUserImages = async () => {
    if (!auth.currentUser) return;
    
    try {
      // Brug den effektive bruger-ID baseret på den valgte kunde
      const effectiveUserId = getEffectiveUserId(selectedCustomer);
      
      const combinedImages = [];
      
      // 1. Hent ai_images (dette er billeder kun til AI-genererede opslag)
      const aiImagesRef = collection(db, 'users', effectiveUserId, 'ai_images');
      const aiImagesQuery = query(aiImagesRef, orderBy('uploadedAt', 'desc'));
      const aiImagesSnapshot = await getDocs(aiImagesQuery);
      
      aiImagesSnapshot.forEach((doc) => {
        combinedImages.push({
          id: doc.id,
          ...doc.data(),
          source: 'ai_image'
        });
      });
      
      // 2. Hent almindelige billeder fra images collection
      // Brug samme sorting som backend: lastUsed desc, uploadedAt asc
      const imagesRef = collection(db, 'users', effectiveUserId, 'images');
      const imagesQuery = query(imagesRef, 
        orderBy('lastUsed', 'desc'), 
        orderBy('uploadedAt', 'asc')
      );
      const imagesSnapshot = await getDocs(imagesQuery);
      
      imagesSnapshot.forEach((doc) => {
        combinedImages.push({
          id: doc.id,
          ...doc.data(),
          source: 'image'
        });
      });
      
      // Omvendt rækkefølgen så det mindst brugte billede kommer først
      // (da Firestore sortering giver os lastUsed DESC, men vi vil have det mindst brugte først)
      const imagesSortedForRotation = combinedImages.filter(img => img.source === 'image').reverse();
      const aiImages = combinedImages.filter(img => img.source === 'ai_image');
      
      // Kombiner med AI billeder først, derefter roterede almindelige billeder
      const finalSortedImages = [...aiImages, ...imagesSortedForRotation];
      
      setGalleryImages(finalSortedImages);
      return finalSortedImages; // Returner billederne så de kan bruges i andre funktioner
    } catch (error) {
      console.error('Fejl ved hentning af brugerens billeder:', error);
      return [];
    }
  };

  const fetchPosts = async () => {
    setLoading(true);
    try {
      // Brug getEffectiveUserId i stedet for direkte auth.currentUser.uid
      const effectiveUserId = getEffectiveUserId(selectedCustomer);
      
      // Sørg for at have de seneste billeder
      const currentImages = galleryImages.length > 0 ? 
        galleryImages : 
        await fetchUserImages();
      
      // Direkte Firestore forespørgsel i stedet for at kalde Cloud Function
      const postsCollectionRef = collection(db, 'users', effectiveUserId, 'webapp_posts');
      const q = query(postsCollectionRef, where('status', '==', 'pending'));
      
      // Før vi sætter onSnapshot, gem en reference til om vi er i gang med at fjerne et billede
      const isRemovingImage = post && post.hasOwnProperty('imageId') && post.imageId === null;
      
      // Brug onSnapshot i stedet for getDocs for at få real-time opdateringer
      const unsubscribe = onSnapshot(q, async (querySnapshot) => {
        if (!querySnapshot.empty) {
          // Konverter dokumenterne til et array og sorter efter oprettelsesdato (nyeste først)
          const posts = [];
          querySnapshot.forEach(doc => {
            posts.push({
              id: doc.id,
              ...doc.data(),
              createdAt: doc.data().createdAt?.toDate()?.toISOString() || null
            });
          });
          
          // Sorter efter oprettelsesdato (nyeste først)
          posts.sort((a, b) => {
            if (!a.createdAt) return 1;
            if (!b.createdAt) return -1;
            return new Date(b.createdAt) - new Date(a.createdAt);
          });
          
          // Vis det seneste opslag
          if (posts.length > 0) {
            const latestPost = posts[0];
            
            console.log('fetchPosts - Processing latest post:', {
              id: latestPost.id,
              hasImageId: !!latestPost.imageId,
              hasImageUrl: !!latestPost.imageUrl,
              imageRemoved: latestPost.imageRemoved,
              imageId: latestPost.imageId,
              currentSelectedImage: selectedImage?.id
            });
            
            // Hvis vi er ved at fjerne et billede og får et snapshot på samme post ID,
            // sørg for at holde imageId og imageUrl som null
            if (isRemovingImage && post && latestPost.id === post.id) {
              console.log('fetchPosts - Detected image removal in progress, preserving null values');
              setPost({
                ...latestPost,
                imageId: null,
                imageUrl: null
              });
              setSelectedImage(null);
            } else {
              // Sæt opslaget i state
              setPost({
                id: latestPost.id,
                content: latestPost.content,
                imageUrl: latestPost.imageUrl,
                imageId: latestPost.imageId,
                imageRemoved: latestPost.imageRemoved, // Bevar imageRemoved flag
                createdAt: latestPost.createdAt
              });
              
              // Hvis opslaget har et billede, find det i galleriet og vælg det
              if (latestPost.imageId) {
                console.log('fetchPosts - Post has image, finding in gallery:', latestPost.imageId);
                const imageIndex = currentImages.findIndex(img => img.id === latestPost.imageId);
                if (imageIndex !== -1) {
                  setSelectedImage(currentImages[imageIndex]);
                  setCurrentImageIndex(imageIndex);
                  console.log('fetchPosts - Image found in gallery, set as selected');
                } else {
                  // Billedet findes ikke i galleriet - fjern referencen
                  console.log('fetchPosts - Image not found in gallery, removing reference');
                  setSelectedImage(null);
                  setCurrentImageIndex(-1);
                }
              } 
              // ÆNDRET LOGIK: Respekter hvis brugeren bevidst har fjernet billedet
              // DEAKTIVERET: Automatisk billede-tildeling - dette forårsager problemer
              // Automatisk tildeling sker kun i backend ved oprettelse af NYE opslag
              else {
                console.log('fetchPosts - No image assigned to post, respecting user choice');
                setSelectedImage(null);
                setCurrentImageIndex(-1); // Konsistent brug af -1 for intet billede
              }
            }
          } else {
            // Ingen opslag fundet
            setPost(null);
            setSelectedImage(null);
          }
        } else {
          // Ingen opslag fundet
          setPost(null);
          setSelectedImage(null);
        }
        
        setLoading(false);
      }, (error) => {
        console.error('Fejl ved hentning af opslag:', error);
        setErrorMessage('Der opstod en fejl ved hentning af opslag. Prøv igen senere.');
        setLoading(false);
      });
      
      // Returnér unsubscribe-funktionen så vi kan fjerne lytteren når komponenten unmounts
      return unsubscribe;
    } catch (error) {
      console.error('Fejl ved opsætning af posts-listener:', error);
      setErrorMessage('Der opstod en fejl ved opsætning af real-time opdateringer.');
      setLoading(false);
    }
  };

  const generatePost = async () => {
    try {
      setGenerating(true);
      setLoading(true);
      
      // Brug den effektive bruger-ID baseret på den valgte kunde
      const effectiveUserId = getEffectiveUserId(selectedCustomer);
      const userData = await getEffectiveUserData(selectedCustomer);
      
      // Hent AI indstillinger for CTA og emoji frekvens
      let ctaFrequency = 'always';
      let emojiFrequency = 'medium';
      let preferredEmojis = '';
      let includeCTA = true;
      let emojiInstruction = 'Brug en moderat mængde emojis (3-5) i opslaget. Placer dem strategisk for at fremhæve vigtige pointer.';
      
      try {
        const aiSettingsRef = doc(db, 'users', effectiveUserId, 'settings', 'ai_prompts');
        const aiMemoryRef = doc(db, 'users', effectiveUserId, 'settings', 'ai_memory');
        
        const [aiSettingsDoc, aiMemoryDoc] = await Promise.all([
          getDoc(aiSettingsRef),
          getDoc(aiMemoryRef)
        ]);
        
        if (aiSettingsDoc.exists()) {
          const settings = aiSettingsDoc.data();
          if (settings.ctaFrequency) ctaFrequency = settings.ctaFrequency;
          if (settings.emojiFrequency) emojiFrequency = settings.emojiFrequency;
          if (settings.preferredEmojis) preferredEmojis = settings.preferredEmojis;
        }
        
        // Bestem CTA baseret på postCount og frekvens
        const currentPostNumber = aiMemoryDoc.exists() ? (aiMemoryDoc.data().postCount || 0) + 1 : 1;
        
        if (ctaFrequency === 'never') {
          includeCTA = false;
        } else if (ctaFrequency === 'every2') {
          includeCTA = currentPostNumber % 2 === 1;
        } else if (ctaFrequency === 'every3') {
          includeCTA = currentPostNumber % 3 === 1;
        } else if (ctaFrequency === 'every4') {
          includeCTA = currentPostNumber % 4 === 1;
        }
        
        // Generer emoji instruktion
        if (emojiFrequency === 'none') {
          emojiInstruction = 'Brug INGEN emojis i opslaget. Hold teksten helt fri for emojis.';
        } else if (emojiFrequency === 'few') {
          emojiInstruction = 'Brug meget få emojis (maksimalt 1-2) i opslaget. Vær diskret med emoji-brugen.';
        } else if (emojiFrequency === 'many') {
          emojiInstruction = 'Brug rigeligt med emojis (6+) i opslaget. Gør teksten visuelt levende med emojis ved mange pointer og afsnit.';
        }
        
        if (preferredEmojis && preferredEmojis.trim()) {
          emojiInstruction += ` Prioritér brugerens foretrukne emojis: ${preferredEmojis}`;
        }
        
        console.log(`AI Generator - Opslag ${currentPostNumber} - CTA: ${includeCTA} (${ctaFrequency}) - Emoji: ${emojiFrequency}`);
        
      } catch (error) {
        console.error('Fejl ved hentning af AI indstillinger:', error);
      }
      
      // Generere et LinkedIn opslag ved hjælp af Claude med CTA logik
      const prompt = generatePrompt(includeCTA, emojiInstruction);
      
      // Lad ClaudeService håndtere sprogvalg baseret på postLanguage indstillingen
      const generatedPost = await ClaudeService.generateResponse(prompt, null, userSettings?.postLanguage);
      
      setPost(generatedPost);
      setEditing(false);
      setGenerating(false);
    } catch (error) {
      console.error('Fejl ved generering af opslag:', error);
      setErrorMessage('Der opstod en fejl under generering af opslaget. Prøv igen senere.');
      setGenerating(false);
    } finally {
      setLoading(false);
    }
  };

  const generatePrompt = (includeCTA = true, emojiInstruction = '') => {
    // Hent brugerens sprogindstilling eller brug standard
    const postLanguage = userSettings?.postLanguage || 'da';
    const promptsForLanguage = getPromptByLanguage(postLanguage);
    
    // En simplificeret version af prompten der bruges i systemet
    const userName = auth.currentUser?.displayName || "brugeren";
    const formattedDate = new Date().toLocaleDateString('da-DK', {
      weekday: 'long', 
      year: 'numeric', 
      month: 'long', 
      day: 'numeric'
    });
    
    // Byg prompt baseret på sprogfilen
    let prompt = promptsForLanguage.advancedSystemPrompt.base;
    
    // Erstat placeholders
    prompt = prompt
      .replace('BRUGERNAVN', userName)
      .replace('DATO', formattedDate);

    // Tilføj produkter hvis de findes
    if (userSettings && userSettings.products && userSettings.products.length > 0) {
      prompt += `\n\n${promptsForLanguage.advancedSystemPrompt.productsIntro}`;
      userSettings.products.forEach(product => {
        prompt += `\n- ${product.name}: ${product.description}`;
      });
    }
    
    // Tilføj lead magnets hvis de findes
    if (userSettings && userSettings.leadMagnets && userSettings.leadMagnets.length > 0) {
      prompt += `\n\n${promptsForLanguage.advancedSystemPrompt.leadMagnetsIntro}`;
      userSettings.leadMagnets.forEach(magnet => {
        prompt += `\n- ${magnet.name}: ${magnet.description}`;
      });
    }

    // Tilføj CTA og emoji instruktioner
    prompt += `\n\nVIGTIGT for dette opslag:
1. ${includeCTA ? promptsForLanguage.ctaInstructions.include : promptsForLanguage.ctaInstructions.exclude}
2. ${emojiInstruction}`;

    // Tilføj user prompt
    prompt += `\n\n${promptsForLanguage.userPrompt}`;
    
    return prompt;
  };

  // Håndter klik på indhold for at starte redigering 
  const handleContentClick = () => {
    // Start redigering
    setEditing(true);
    setEditText(post.content);
    
    // Vi bruger setTimeout for at sikre at komponenten har opdateret sin state
    // og textarea er blevet renderet før vi forsøger at placere cursoren
    setTimeout(() => {
      if (!textareaRef.current) return;
      
      // Bare fokusér på textarea
      textareaRef.current.focus();
      
      // Resize textarea til indholdet
      resizeTextarea(textareaRef.current);
    }, 10);
  };

  // Håndter ændringer i textarea for at auto-resize
  const handleTextareaChange = (e) => {
    setEditText(e.target.value);
    resizeTextarea(e.target);
  };
  
  // Håndter tastaturinteraktioner i textarea
  const handleTextareaKeyDown = (e) => {
    // Gem ved Ctrl+Enter eller Cmd+Enter
    if ((e.ctrlKey || e.metaKey) && e.key === 'Enter') {
      e.preventDefault();
      handleSaveEdit();
    }
    
    // Luk redigering ved Escape
    if (e.key === 'Escape') {
      e.preventDefault();
      setEditing(false);
    }
  };

  // Funktion for at sikre at textarea altid tilpasses indholdet
  useEffect(() => {
    if (textareaRef.current && editing) {
      resizeTextarea(textareaRef.current);
    }
  }, [editing, editText]);

  const handleSaveEdit = async () => {
    try {
      setLoading(true);
      
      // Manuel redigering - gem direkte til databasen
      const userId = auth.currentUser.uid;
      const postRef = doc(db, 'users', userId, 'webapp_posts', post.id);
      
      await updateDoc(postRef, {
        content: editText,
        updatedAt: serverTimestamp()
      });
      
      // Opdater lokalt state
      setPost({
        ...post,
        content: editText
      });
      
      setEditing(false); // Luk redigeringstilstand
      setLoading(false);
      setSuccessMessage(t('aiGenerator', 'messages.savedChanges'));
      setTimeout(() => setSuccessMessage(null), 2000);
    } catch (error) {
      console.error('Fejl ved redigering af opslag:', error);
      // Log detaljer om fejlen for bedre debugging
      if (error.code && error.message) {
        console.error(`Firebase fejlkode: ${error.code}, besked: ${error.message}`);
      }
      if (error.details) {
        console.error('Fejldetaljer:', error.details);
      }
      setErrorMessage(t('aiGenerator', 'messages.error'));
      setLoading(false);
      setEditing(false);
    }
  };

  const handleRetry = async () => {
    if (!auth.currentUser || !post) return;
    
    try {
      setGenerating(true);
      
      // Hent brugeropysninger til at generere en prompt
      const userId = auth.currentUser.uid;
      const userDoc = await getDoc(doc(db, 'users', userId));
      const userData = userDoc.data() || {};
      
      // Hent tidligere opslag hvis tilgængelige
      const topPostsQuery = query(collection(db, 'users', userId, 'linkedin_top_posts'), limit(3));
      const topPostsSnapshot = await getDocs(topPostsQuery);
      const topPosts = [];
      topPostsSnapshot.forEach(doc => {
        topPosts.push(doc.data());
      });
      
      // Generer en prompt til Claude/OpenAI
      const prompt = generateAIPrompt(userData, topPosts);
      
      // Generer nyt indhold med Claude - send postLanguage så det korrekte sprog bruges
      const newContent = await ClaudeService.generateResponse(prompt, null, userSettings?.postLanguage);
      
      // Opdater opslaget i databasen
      const postRef = doc(db, 'users', userId, 'webapp_posts', post.id);
      await updateDoc(postRef, {
        content: newContent,
        updatedAt: serverTimestamp()
      });
      
      // Opdater lokalt state med det nye indhold
      setPost({
        ...post,
        content: newContent
      });
      
      setSuccessMessage(t('aiGenerator', 'messages.regenerated'));
      setTimeout(() => setSuccessMessage(null), 2000);
      setGenerating(false);
    } catch (error) {
      console.error('Fejl ved regenerering af opslag:', error);
      setErrorMessage(t('aiGenerator', 'messages.error'));
      setGenerating(false);
    }
  };

  // Hjælpefunktion til at generere en prompt
  const generateAIPrompt = (userData, topPosts) => {
    // Hent brugerens sprogindstilling eller brug standard
    const postLanguage = userSettings?.postLanguage || 'da';
    const promptsForLanguage = getPromptByLanguage(postLanguage);
    
    const name = userData.displayName || userData.name || 'brugeren';
    const jobTitle = userData.jobTitle || '';
    const companyName = userData.companyName || '';
    
    // Eksempler tekst fra tidligere opslag
    let examplesText = '';
    if (topPosts && topPosts.length > 0) {
      examplesText = `${promptsForLanguage.templates?.examplesIntro || 'Her er eksempler på populære LinkedIn-opslag (brug kun som inspiration til tone og stil):'}`;
      topPosts.forEach((post, index) => {
        examplesText += `\n\n${promptsForLanguage.templates?.examplePrefix || 'Eksempel'} ${index + 1}:\n"${post.content}"`;
      });
    }
    
    // Byg system prompt baseret på sprogfilen  
    let prompt = promptsForLanguage.systemPrompt;
    
    // Tilføj brugerinformation
    let userTemplate = '';
    if (jobTitle && companyName) {
      userTemplate = promptsForLanguage.templates?.userWithTitleAndCompany || `Din opgave er at skrive et LinkedIn-opslag for ${name}, som er ${jobTitle} hos ${companyName}`;
      userTemplate = userTemplate.replace('BRUGERNAVN', name).replace('JOBTITEL', jobTitle).replace('FIRMANAVN', companyName);
    } else if (jobTitle) {
      userTemplate = promptsForLanguage.templates?.userWithTitle || `Din opgave er at skrive et LinkedIn-opslag for ${name}, som er ${jobTitle}`;
      userTemplate = userTemplate.replace('BRUGERNAVN', name).replace('JOBTITEL', jobTitle);
    } else if (companyName) {
      userTemplate = promptsForLanguage.templates?.userWithCompany || `Din opgave er at skrive et LinkedIn-opslag for ${name} hos ${companyName}`;
      userTemplate = userTemplate.replace('BRUGERNAVN', name).replace('FIRMANAVN', companyName);
    } else {
      userTemplate = promptsForLanguage.templates?.userOnly || `Din opgave er at skrive et LinkedIn-opslag for ${name}`;
      userTemplate = userTemplate.replace('BRUGERNAVN', name);
    }
    
    prompt += `\n\n${userTemplate}`;
    
    // Tilføj standard instruktioner
    prompt += `\n\n${promptsForLanguage.templates?.postInstructions || `Skab et opslag der er:
- Personligt og autentisk
- Relevant for et professionelt publikum
- Velskrevet med en uformel tone
- Struktureret med korte afsnit for god læsevenlighed
- Mellem 100-200 ord`}`;

    // Tilføj eksempler hvis tilgængelige
    if (examplesText) {
      prompt += `\n\n${examplesText}`;
    }
    
    // Tilføj produkter hvis tilgængelige
    if (userData.products && userData.products.length > 0) {
      prompt += `\n\n${promptsForLanguage.templates?.productsIntro || 'Dette er produkter eller services jeg arbejder med (brug som baggrundsviden, men lav ikke en salgstale for dem):'}`;
      userData.products.forEach(product => {
        prompt += `\n- ${product.name}: ${product.description}`;
      });
    }
    
    // Tilføj finale noter
    prompt += `\n\n${promptsForLanguage.templates?.finalNotes || `VIGTIGT:
- Start IKKE med "Hej LinkedIn" eller lignende hilsner
- Undgå at starte med en emoji, det ser unaturligt ud
- Skriv direkte som mig i første person
- Gør ikke opslaget til en åbenlys salgstale
- Undgå at skrive "hashtags" ind i teksten
- Skriv kun selve opslagets tekst uden introduktion eller forklaring
- Afslut ikke afsnit med punktum, kun det sidste afsnit`}`;
    
    return prompt;
  };

  const handleSelectImage = async (image) => {
    if (!auth.currentUser || !post) return;
    
    setSelectedImage(image);
    setGalleryOpen(false);
    
    // Find billedets index i galleriet
    const imageIndex = galleryImages.findIndex(img => img.id === image.id);
    if (imageIndex !== -1) {
      setCurrentImageIndex(imageIndex);
    }
    
    try {
      // Gem billedet i databasen med det samme
      const userId = getEffectiveUserId(selectedCustomer);
      const postRef = doc(db, 'users', userId, 'webapp_posts', post.id);
      
      await updateDoc(postRef, {
        imageId: image.id,
        imageUrl: image.downloadURL,
        imageRemoved: deleteField(), // Fjern imageRemoved flag når billede vælges
        updatedAt: serverTimestamp()
      });
      
      // Opdater lastUsed tidsstemplet for billedet
      const imageRef = doc(db, 'users', userId, 'images', image.id);
      await updateDoc(imageRef, {
        lastUsed: serverTimestamp()
      });
      
      // Opdater lokalt post objekt og fjern imageRemoved flag
      const updatedPost = { ...post };
      delete updatedPost.imageRemoved; // Fjern imageRemoved flag fra lokalt objekt
      setPost({
        ...updatedPost,
        imageId: image.id,
        imageUrl: image.downloadURL
      });
      
      setSuccessMessage(t('aiGenerator', 'messages.imageUpdated'));
      setTimeout(() => setSuccessMessage(null), 2000);
    } catch (error) {
      console.error('Fejl ved opdatering af billede:', error);
      setErrorMessage(t('aiGenerator', 'messages.error'));
      setTimeout(() => setErrorMessage(null), 3000);
    }
  };
  
  const handleNextImage = async () => {
    if (!auth.currentUser || !post || galleryImages.length === 0) return;
    
    // Find næste billede i rotationscyklen
    // Hvis currentImageIndex er -1 (intet billede valgt), start fra det første billede (index 0)
    let nextIndex;
    if (currentImageIndex === -1) {
      nextIndex = 0; // Start fra første billede
    } else {
      nextIndex = (currentImageIndex + 1) % galleryImages.length;
    }
    
    const nextImage = galleryImages[nextIndex];
    
    setCurrentImageIndex(nextIndex);
    setSelectedImage(nextImage);
    
    try {
      const userId = getEffectiveUserId(selectedCustomer);
      const postRef = doc(db, 'users', userId, 'webapp_posts', post.id);
      
      // Opdater opslaget med det nye billede og fjern imageRemoved flag
      await updateDoc(postRef, {
        imageId: nextImage.id,
        imageUrl: nextImage.downloadURL,
        imageRemoved: deleteField(), // Fjern imageRemoved flag når billede tilføjes
        updatedAt: serverTimestamp()
      });
      
      // KRITISK: Opdater lastUsed tidsstemplet for billedet for at indikere at det er brugt
      const imageRef = doc(db, 'users', userId, 'images', nextImage.id);
      await updateDoc(imageRef, {
        lastUsed: serverTimestamp()
      });
      
      // Opdater lokalt post objekt og fjern imageRemoved flag
      const updatedPost = { ...post };
      delete updatedPost.imageRemoved; // Fjern imageRemoved flag fra lokalt objekt
      setPost({
        ...updatedPost,
        imageId: nextImage.id,
        imageUrl: nextImage.downloadURL
      });
      
      console.log(`Skiftet til næste billede: ${nextImage.id}, opdateret lastUsed timestamp`);
      
      setSuccessMessage('Billede opdateret!');
      setTimeout(() => setSuccessMessage(null), 2000);
    } catch (error) {
      console.error('Fejl ved opdatering af billede:', error);
      setErrorMessage('Der opstod en fejl ved opdatering af billedet.');
      setTimeout(() => setErrorMessage(null), 3000);
    }
  };
  
  const handleRemoveImage = async () => {
    if (!auth.currentUser || !post) return;
    
    console.log('handleRemoveImage - Starting image removal for post:', post.id);
    
    // Sæt lokale states med det samme for bedre brugeroplevelse
    setSelectedImage(null);
    setCurrentImageIndex(-1); // Sæt til -1 for at indikere at intet billede er valgt
    
    // Opdater også lokalt post objekt for at sikre at UI afspejler ændringen øjeblikkeligt
    const updatedPost = {
      ...post,
      imageId: null,
      imageUrl: null,
      imageRemoved: true // Flag der indikerer at brugeren bevidst har fjernet billedet
    };
    setPost(updatedPost);
    
    try {
      // Brug den effektive bruger-ID baseret på den valgte kunde
      const userId = getEffectiveUserId(selectedCustomer);
      const postRef = doc(db, 'users', userId, 'webapp_posts', post.id);
      
      console.log('handleRemoveImage - Updating database with imageRemoved flag');
      
      await updateDoc(postRef, {
        imageId: deleteField(), // Slet feltet fuldstændigt
        imageUrl: deleteField(), // Slet feltet fuldstændigt
        imageRemoved: true, // Marker i databasen at billedet er fjernet bevidst
        updatedAt: serverTimestamp()
      });
      
      console.log('handleRemoveImage - Database updated successfully');
      
      setSuccessMessage(t('aiGenerator', 'messages.imageRemoved'));
      setTimeout(() => setSuccessMessage(null), 2000);
    } catch (error) {
      console.error('Fejl ved fjernelse af billede:', error);
      setErrorMessage(t('aiGenerator', 'messages.error'));
      setTimeout(() => setErrorMessage(null), 3000);
      
      // Hvis databaseopdatering fejler, tilbagefør lokale ændringer
      setPost(post);
      setSelectedImage(post.imageId ? galleryImages.find(img => img.id === post.imageId) : null);
    }
  };

  // Opdateret hjælpefunktion til at pre-beregne flere ledige tidspunkter
  const getNextAvailableScheduledTimes = async (userId, count = 5) => {
    try {
      // Hent brugerens scheduler-indstillinger
      const scheduleRef = doc(db, 'users', userId, 'settings', 'schedule');
      const scheduleDoc = await getDoc(scheduleRef);
      
      if (!scheduleDoc.exists() || !scheduleDoc.data().postSchedule) {
        console.log('Ingen scheduler-indstillinger fundet');
        return [];
      }
      
      const scheduleData = scheduleDoc.data();
      const timezone = scheduleData.timezone || 'UTC';
      const postSchedule = scheduleData.postSchedule;
      
      // Hent ALLE eksisterende planlagte opslag (både autoScheduled og customScheduled)
      const postsRef = collection(db, 'users', userId, 'posts');
      const existingPostsQuery = query(
        postsRef,
        where('status', 'in', ['scheduled', 'ready']) // Inkluder både scheduled og ready
      );
      const existingPosts = await getDocs(existingPostsQuery);
      
      // Samle alle optagne tidspunkter med mere præcision
      const occupiedTimes = new Set();
      existingPosts.forEach(doc => {
        const postData = doc.data();
        // Tjek både scheduledDateTime og scheduledDate
        const scheduledDateTime = postData.scheduledDateTime || postData.scheduledDate;
        if (scheduledDateTime) {
          // Konverter til timestamp og rund ned til det nærmeste minut
          const timestamp = new Date(scheduledDateTime.seconds * 1000);
          timestamp.setSeconds(0, 0); // Nulstil sekunder og millisekunder
          occupiedTimes.add(timestamp.getTime());
          
          console.log(`Optaget tidspunkt: ${timestamp.toISOString()} (opslag: ${doc.id})`);
        }
      });
      
      console.log(`Fandt ${occupiedTimes.size} optagne tidspunkter`);
      
      // Find de næste ledige tidspunkter
      const availableTimes = [];
      const now = new Date();
      let currentDate = new Date(now);
      currentDate.setHours(0, 0, 0, 0);
      
      // Søg op til 8 uger frem (mere fleksibilitet)
      for (let daysFromNow = 0; daysFromNow < 56 && availableTimes.length < count; daysFromNow++) {
        const checkDate = new Date(currentDate);
        checkDate.setDate(currentDate.getDate() + daysFromNow);
        
        const dayName = ['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday'][checkDate.getDay()];
        const scheduledTimes = postSchedule[dayName] || [];
        
        // Sortér tiderne så vi tager dem i rækkefølge
        const sortedTimes = [...scheduledTimes].sort();
        
        // Tjek hver planlagt tid for denne dag
        for (const timeString of sortedTimes) {
          if (availableTimes.length >= count) break;
          
          const [hours, minutes] = timeString.split(':').map(Number);
          const potentialDateTime = new Date(checkDate);
          potentialDateTime.setHours(hours, minutes, 0, 0);
          
          // Skip hvis tidspunktet er i fortiden (med 5 minutters buffer)
          const fiveMinutesFromNow = new Date(now.getTime() + 5 * 60 * 1000);
          if (potentialDateTime <= fiveMinutesFromNow) {
            console.log(`Springer over fortid tidspunkt: ${potentialDateTime.toISOString()}`);
            continue;
          }
          
          // Tjek om dette tidspunkt allerede er optaget
          const potentialTime = potentialDateTime.getTime();
          if (!occupiedTimes.has(potentialTime)) {
            console.log(`Fandt ledigt tidspunkt: ${potentialDateTime.toISOString()}`);
            
            // Tilføj til listen og marker som optaget for næste iteration
            availableTimes.push(Timestamp.fromDate(potentialDateTime));
            occupiedTimes.add(potentialTime);
          } else {
            console.log(`Tidspunkt allerede optaget: ${potentialDateTime.toISOString()}`);
          }
        }
      }
      
      console.log(`Fandt ${availableTimes.length} ledige tidspunkter`);
      return availableTimes;
      
    } catch (error) {
      console.error('Fejl ved hentning af ledige planlagte tidspunkter:', error);
      return [];
    }
  };

  // Pre-beregn tidspunkter når posts hentes
  useEffect(() => {
    const preCalculateScheduledTimes = async () => {
      if (!auth.currentUser || galleryImages.length === 0) return;
      
      setTimeCalculationLoading(true);
      try {
        const effectiveUserId = getEffectiveUserId(selectedCustomer);
        if (!effectiveUserId) return;
        
        // Beregn 5 tidspunkter så vi har nok til alle poster
        const availableTimes = await getNextAvailableScheduledTimes(effectiveUserId, 5);
        setPreCalculatedTimes(availableTimes);
        console.log(`Pre-beregnede ${availableTimes.length} tidspunkter`);
      } catch (error) {
        console.error('Fejl ved pre-beregning af tidspunkter:', error);
      } finally {
        setTimeCalculationLoading(false);
      }
    };
    
    preCalculateScheduledTimes();
  }, [galleryImages.length, selectedCustomer]); // Genberegn når antal posts ændres

  const handleAcceptPost = async () => {
    if (!auth.currentUser || !post) return;
    
    // Tjek om vi har pre-beregnede tidspunkter
    if (preCalculatedTimes.length === 0) {
      setErrorMessage(t('aiGenerator', 'messages.calculatingTimes'));
      return;
    }
    
    setIsSaving(true);
    
    try {
      // Brug den effektive bruger-ID baseret på den valgte kunde
      const effectiveUserId = getEffectiveUserId(selectedCustomer);
      const userData = await getEffectiveUserData(selectedCustomer);
      
      // Brug det første pre-beregnede tidspunkt
      const nextScheduledTime = preCalculatedTimes[0];
      
      if (!nextScheduledTime) {
        setErrorMessage(t('aiGenerator', 'messages.noScheduledTimes'));
        setIsSaving(false);
        return;
      }
      
      // Forbered opdateringsobjekt
      const updateData = {
        status: 'accepted',
        updatedAt: serverTimestamp()
      };
      
      // Kun opdater billede-felter hvis brugeren ikke bevidst har fjernet billedet
      if (!post.imageRemoved) {
        updateData.imageId = selectedImage ? selectedImage.id : null;
        updateData.imageUrl = selectedImage ? selectedImage.downloadURL : null;
      }
      // Hvis billedet er fjernet bevidst, bevar de eksisterende værdier (som er null/deleted)
      
      // Opdater status af opslaget
      const postRef = doc(db, 'users', effectiveUserId, 'webapp_posts', post.id);
      await updateDoc(postRef, updateData);
      
      // Forbered data til posts collection med specifikt planlagt tidspunkt
      const postsData = {
        content: post.content,
        createdAt: serverTimestamp(),
        status: 'scheduled',
        scheduledDateTime: nextScheduledTime, // Brug pre-beregnet tidspunkt
        isPublished: false,
        autoScheduled: true // Flag der indikerer at dette er automatisk planlagt
      };
      
      // Kun tilføj billede-data til posts collection hvis brugeren ikke har fjernet billedet
      if (!post.imageRemoved && selectedImage) {
        postsData.imageId = selectedImage.id;
        postsData.imageUrl = selectedImage.downloadURL;
      }
      
      // Kopier opslaget til users/{userId}/posts collection til brug i Posts komponenten
      const postsCollectionRef = collection(db, 'users', effectiveUserId, 'posts');
      await addDoc(postsCollectionRef, postsData);
      
      // Fjern det brugte tidspunkt fra listen
      setPreCalculatedTimes(prev => prev.slice(1));
      
      // Hvis der er færre end 2 tidspunkter tilbage, beregn flere
      if (preCalculatedTimes.length <= 2) {
        const additionalTimes = await getNextAvailableScheduledTimes(effectiveUserId, 3);
        setPreCalculatedTimes(prev => [...prev.slice(1), ...additionalTimes]);
      }
      
      // Hvis der er valgt et billede og det ikke er fjernet, opdater lastUsed-tidsstemplet
      if (selectedImage && !post.imageRemoved) {
        const imageRef = doc(db, 'users', effectiveUserId, 'images', selectedImage.id);
        await updateDoc(imageRef, {
          lastUsed: serverTimestamp()
        });
      }
      
      // Vis succesmeddelelse med det planlagte tidspunkt
      const scheduledDate = new Date(nextScheduledTime.seconds * 1000);
      const formattedDate = scheduledDate.toLocaleDateString('da-DK', {
        weekday: 'long',
        day: 'numeric',
        month: 'long',
        hour: '2-digit',
        minute: '2-digit'
      });
      
      setSuccessMessage(t('aiGenerator', 'messages.postScheduled', { formattedDate }));
      
      // Tjek for flere opslag
      await fetchPosts();
      
      // Fjern succesmeddelelsen efter 5 sekunder
      setTimeout(() => {
        setSuccessMessage(null);
      }, 5000);
      
      // VIGTIGT: Nulstil loading state efter success
      setIsSaving(false);
    } catch (error) {
      console.error('Fejl ved accept af opslag:', error);
      setErrorMessage(t('aiGenerator', 'messages.error'));
      setIsSaving(false);
    }
  };

  const handleAIPrompt = async () => {
    try {
      setAiEditing(true);
      
      // Hent bruger-ID
      if (!auth.currentUser) {
        setErrorMessage(t('aiGenerator', 'messages.error'));
        return;
      }
      
      // Hent sprogindstilling
      const postLanguage = userSettings?.postLanguage || 'da';
      const promptsForLanguage = getPromptByLanguage(postLanguage);
      
      // Behandl brugerprompt
      const prompt = `${promptsForLanguage.editPrompt}

Eksisterende opslag:
"${post.content}"

Bruger instruktioner: "${aiInstructions}"

${promptsForLanguage.edit.finalNote || 'Skriv kun det opdaterede opslag uden yderligere forklaringer eller introduktioner.'}`;
      
      // Brug ClaudeService direkte med sproget
      const newContent = await ClaudeService.generateResponse(prompt, null, postLanguage);
      
      // Opdater opslaget i databasen
      if (post && post.id) {
        const postRef = doc(db, 'users', auth.currentUser.uid, 'webapp_posts', post.id);
        await updateDoc(postRef, {
          content: newContent,
          updatedAt: serverTimestamp()
        });
        
        // Opdater lokalt state med det nye indhold
        setPost({
          ...post,
          content: newContent
        });
        
        setSuccessMessage(t('aiGenerator', 'messages.updated'));
      } else {
        // Gem som nyt opslag
        const postRef = await addDoc(collection(db, 'users', auth.currentUser.uid, 'webapp_posts'), {
          content: newContent,
          createdAt: serverTimestamp(),
          updatedAt: serverTimestamp(),
          status: 'pending',
          isRead: true
        });
        
        setPost({
          id: postRef.id,
          content: newContent,
          createdAt: new Date().toISOString()
        });
        
        setSuccessMessage(t('aiGenerator', 'messages.newPost'));
      }
      
      setTimeout(() => setSuccessMessage(null), 2000);
      // Nulstil input felt
      setAiInstructions('');
    } catch (error) {
      console.error('Fejl ved AI prompt:', error);
      setErrorMessage(t('aiGenerator', 'messages.error'));
    } finally {
      setAiEditing(false);
    }
  };

  // Funktion til at håndtere upload af et nyt billede
  const handleImageUpload = async (file) => {
    if (!auth.currentUser || !post || !file) return;
    
    try {
      setUploading(true);
      const userId = auth.currentUser.uid;
      
      // Generer et unikt filnavn
      const fileName = `ai_${new Date().getTime()}_${file.name}`;
      // Gem i en anden mappe i Storage
      const storageRef = ref(storage, `users/${userId}/ai_images/${fileName}`);
      
      // Upload fil til Firebase Storage
      await uploadBytes(storageRef, file);
      
      // Få download URL
      const downloadURL = await getDownloadURL(storageRef);
      
      // Gem billede i en separat ai_images collection
      const imageData = {
        name: fileName,
        downloadURL: downloadURL,
        contentType: file.type,
        size: file.size,
        uploadedAt: serverTimestamp(),
        lastUsed: serverTimestamp()
      };
      
      // Tilføj billedet til ai_images collection
      const imageDocRef = await addDoc(collection(db, 'users', userId, 'ai_images'), imageData);
      
      // Tilføj ID til imageData
      const newImage = {
        id: imageDocRef.id,
        ...imageData
      };
      
      // Tilføj billedet til galleryImages
      setGalleryImages(prev => [newImage, ...prev]);
      
      // Sæt det nye billede som selectedImage
      setSelectedImage(newImage);
      
      // Opdater opslaget med det nye billede
      const postRef = doc(db, 'users', userId, 'webapp_posts', post.id);
      await updateDoc(postRef, {
        imageId: imageDocRef.id,
        imageUrl: downloadURL,
        updatedAt: serverTimestamp()
      });
      
      // Opdater lokalt post objekt
      setPost({
        ...post,
        imageId: imageDocRef.id,
        imageUrl: downloadURL
      });
      
      setSuccessMessage(t('aiGenerator', 'messages.imageUploaded'));
      setTimeout(() => setSuccessMessage(null), 2000);
      
      // Nulstil UI states
      setImageOptionsOpen(false);
    } catch (error) {
      console.error('Fejl ved upload af billede:', error);
      setErrorMessage('Der opstod en fejl ved upload af billedet. Prøv igen senere.');
      setTimeout(() => setErrorMessage(null), 3000);
    } finally {
      setUploading(false);
    }
  };
  
  // Håndter fil-valg fra input-element
  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      handleImageUpload(file);
    }
  };
  
  // Håndter billede fra kamera
  const handleCameraCapture = (e) => {
    const file = e.target.files[0];
    if (file) {
      handleImageUpload(file);
    }
  };
  
  // Åbn filinput
  const triggerFileInput = () => {
    fileInputRef.current?.click();
  };
  
  // Åbn kamera
  const triggerCameraInput = () => {
    cameraInputRef.current?.click();
  };

  return (
    <div className="dashboard-layout">
      <Menu onLogout={onLogout} />
      <div className={`ai-generator-container ${isMenuCollapsed ? 'menu-collapsed' : ''}`}>
        <div className="aig-centered-content">
          {/* Vis banner, hvis man arbejder på vegne af kunde */}
          {isWorkingOnBehalfOfCustomer && (
            <div className="customer-action-banner">
              <i className="fas fa-user-friends"></i>
              <span>{t('aiGenerator', 'customerBanner.working')} <strong>{selectedCustomer.name || selectedCustomer.email}</strong></span>
            </div>
          )}
          
          <div className="ai-generator-header">
            <h1>{t('aiGenerator', 'header.title')}</h1>
            <p className="ai-generator-description">
              {t('aiGenerator', 'header.description')}
            </p>
          </div>
          
          {/* Forenklet indhold */}
          {loading ? (
            <div className="aig-loading">
              <i className="fas fa-spinner fa-spin"></i>
              <p>{t('aiGenerator', 'loading.posts')}</p>
            </div>
          ) : !post ? (
            <div className="aig-no-posts">
              <i className="fas fa-robot"></i>
              <h3>{t('aiGenerator', 'noPosts.title')}</h3>
              <p>
                {t('aiGenerator', 'noPosts.description')}
              </p>
              <p>
                {t('aiGenerator', 'noPosts.settings')} <a href="/linkedin-settings">{t('aiGenerator', 'noPosts.settingsLink')}</a> {t('aiGenerator', 'noPosts.settingsAfter')}
              </p>
            </div>
          ) : (
            <>
              <div className="simple-post-container">
                <div className="simple-post-card">
                  <div className="aig-post-header">
                    <div className="aig-post-author">
                      <div className="aig-author-avatar">
                        <i className="fas fa-user"></i>
                      </div>
                      <div className="aig-author-info">
                        <p className="aig-author-name">{auth.currentUser?.displayName || auth.currentUser?.email || t('aiGenerator', 'post.author')}</p>
                        <p className="aig-post-time">{t('aiGenerator', 'post.time')} <i className="fas fa-globe"></i></p>
                      </div>
                    </div>
                  </div>
                  <div className="aig-post-container">
                    {editing ? (
                      <div className="aig-edit-container">
                        <textarea 
                          className="aig-edit-textarea"
                          value={editText}
                          onChange={handleTextareaChange}
                          onKeyDown={handleTextareaKeyDown}
                          onBlur={() => {
                            // Gem kun hvis der er ændringer
                            if (editText.trim() !== post.content.trim()) {
                              handleSaveEdit();
                            } else {
                              setEditing(false);
                            }
                          }}
                          autoFocus
                          ref={textareaRef}
                          spellCheck="true"
                        />
                        <div className="aig-edit-hint">
                          <span>{t('aiGenerator', 'post.editHint')}</span>
                        </div>
                      </div>
                    ) : (
                      <div className="aig-post-content" onClick={handleContentClick}>
                        {post.content}
                      </div>
                    )}
                    
                    {selectedImage && (
                      <div className="aig-post-image">
                        <img src={selectedImage.downloadURL} alt="Opslag billede" />
                      </div>
                    )}
                  </div>
                  <div className="aig-post-stats">
                    <div className="aig-stat">
                      <i className="far fa-thumbs-up"></i> {t('aiGenerator', 'stats.likes')}
                    </div>
                    <div className="aig-stat">
                      <i className="far fa-comment"></i> {t('aiGenerator', 'stats.comments')}
                    </div>
                    <div className="aig-stat">
                      <i className="far fa-share-square"></i> {t('aiGenerator', 'stats.shares')}
                    </div>
                  </div>
                </div>
              </div>
            </>
          )}
        </div>

        {!editing && post && (
          <div className="simple-bottom-bar">
            <div className="minimalist-input-row">
              <input
                type="text"
                className="minimalist-input"
                placeholder={t('aiGenerator', 'aiPrompt.placeholder')}
                value={aiInstructions}
                onChange={(e) => setAiInstructions(e.target.value)}
                disabled={generating || aiEditing}
              />
              <button 
                className="minimalist-button minimalist-button-send" 
                onClick={handleAIPrompt}
                disabled={!aiInstructions.trim() || generating || aiEditing}
              >
                {aiEditing ? (
                  <><i className="fas fa-spinner fa-spin"></i> <span className="button-text">{t('aiGenerator', 'aiPrompt.editing')}</span></>
                ) : (
                  <><i className="fas fa-magic"></i> <span className="button-text">{t('aiGenerator', 'aiPrompt.send')}</span></>
                )}
              </button>
            </div>
            <div className="minimalist-action-row">
              <button className="minimalist-button minimalist-button-retry" onClick={handleRetry} disabled={generating}>
                {generating ? (
                  <><i className="fas fa-spinner fa-spin"></i> <span className="button-text">{t('aiGenerator', 'buttons.generating')}</span></>
                ) : (
                  <><i className="fas fa-sync-alt"></i> <span className="button-text">{t('aiGenerator', 'buttons.retry')}</span></>
                )}
              </button>
              
              {selectedImage ? (
                <>
                  <button className="minimalist-button minimalist-button-remove" onClick={handleRemoveImage} disabled={generating}>
                    <i className="fas fa-times"></i> <span className="button-text">{t('aiGenerator', 'buttons.removeImage')}</span>
                  </button>
                  <button className="minimalist-button minimalist-button-image" onClick={handleNextImage} disabled={generating}>
                    <i className="fas fa-arrow-right"></i> <span className="button-text">{t('aiGenerator', 'buttons.nextImage')}</span>
                  </button>
                </>
              ) : (
                <button className="minimalist-button minimalist-button-image" onClick={() => setImageOptionsOpen(true)} disabled={generating}>
                  <i className="fas fa-image"></i> <span className="button-text">{t('aiGenerator', 'buttons.addImage')}</span>
                </button>
              )}
              
              <button className="minimalist-button minimalist-button-accept" onClick={handleAcceptPost} disabled={generating || isSaving || timeCalculationLoading || preCalculatedTimes.length === 0}>
                {isSaving ? (
                  <><i className="fas fa-spinner fa-spin"></i> <span className="button-text">{t('aiGenerator', 'buttons.scheduling')}</span></>
                ) : timeCalculationLoading ? (
                  <><i className="fas fa-spinner fa-spin"></i> <span className="button-text">{t('aiGenerator', 'buttons.calculatingTimes')}</span></>
                ) : preCalculatedTimes.length === 0 ? (
                  <><i className="fas fa-exclamation-triangle"></i> <span className="button-text">{t('aiGenerator', 'buttons.noAvailableTimes')}</span></>
                ) : (
                  <><i className="fas fa-check"></i> <span className="button-text">{t('aiGenerator', 'buttons.accept')}</span></>
                )}
              </button>
            </div>
          </div>
        )}
        
        {/* Behold de nødvendige modals/overlays */}
        {galleryOpen && (
          <div className="aig-gallery-overlay">
            <div className="aig-gallery-container">
              <div className="aig-gallery-header">
                <h3>{t('aiGenerator', 'gallery.title')}</h3>
                <button className="aig-gallery-close" onClick={() => setGalleryOpen(false)}>
                  <i className="fas fa-times"></i>
                </button>
              </div>
              
              <div className="aig-gallery-content">
                {galleryImages.length > 0 ? (
                  <div className="aig-image-grid">
                    {galleryImages.map((image) => (
                      <div 
                        key={image.id} 
                        className="aig-image-item"
                        onClick={() => handleSelectImage(image)}
                      >
                        <img src={image.downloadURL} alt="Billede" />
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="aig-no-images">
                    <i className="fas fa-images"></i>
                    <p>{t('aiGenerator', 'gallery.noImages')}</p>
                    <a href="/billedearkiv">{t('aiGenerator', 'gallery.goToArchive')}</a>
                  </div>
                )}
              </div>
            </div>
          </div>
        )}
        
        {imageOptionsOpen && (
          <div className="aig-image-options-overlay">
            <div className="aig-image-options-container">
              <div className="aig-image-options-header">
                <h3>{t('aiGenerator', 'imageOptions.title')}</h3>
                <button className="aig-gallery-close" onClick={() => setImageOptionsOpen(false)}>
                  <i className="fas fa-times"></i>
                </button>
              </div>
              <div className="aig-image-options-content">
                <button 
                  className="aig-image-option-button" 
                  onClick={() => {
                    setImageOptionsOpen(false);
                    setGalleryOpen(true);
                  }}
                  disabled={uploading}
                >
                  <i className="fas fa-images"></i>
                  <span>{t('aiGenerator', 'imageOptions.gallery')}</span>
                </button>
                
                <button 
                  className="aig-image-option-button" 
                  onClick={triggerFileInput}
                  disabled={uploading}
                >
                  <i className="fas fa-upload"></i>
                  <span>{t('aiGenerator', 'imageOptions.upload')}</span>
                </button>
                
                <button 
                  className="aig-image-option-button" 
                  onClick={triggerCameraInput}
                  disabled={uploading}
                >
                  <i className="fas fa-camera"></i>
                  <span>{t('aiGenerator', 'imageOptions.camera')}</span>
                </button>
                
                <a 
                  href="/billedearkiv" 
                  className="aig-image-option-button"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  <i className="fas fa-cog"></i>
                  <span>{t('aiGenerator', 'imageOptions.manage')}</span>
                </a>
              </div>
            </div>
          </div>
        )}
        
        {/* Skjulte fileinputs */}
        <input 
          type="file" 
          ref={fileInputRef} 
          style={{ display: 'none' }} 
          accept="image/*"
          onChange={handleFileChange}
        />
        <input 
          type="file" 
          ref={cameraInputRef} 
          style={{ display: 'none' }} 
          accept="image/*"
          capture="environment"
          onChange={handleCameraCapture}
        />
      </div>
    </div>
  );
};

export default AIGenerator; 